import {
  TemplateFormFieldKind,
  TemplateFormFieldProvenance,
  type CaraWorkspaceFieldsFragment,
  type TemplateFormFieldEditInput,
  type TemplateFormFieldStateFieldsFragment
} from '~/api/graphql';
import type { DeclinedSuggestions } from './template-form-dismissals';

export type TemplateFormField = TemplateFormFieldStateFieldsFragment;

/** What the user has typed since the last save, keyed by widget name. */
export type TemplateFormEdits = Readonly<Record<string, string>>;

/** The value an unchecked checkbox posts. The server also accepts the field's own off value. */
export const UNCHECKED_VALUE = 'Off';

export const DISCARD_CHANGES_PROMPT =
  'You have unsaved changes to this form. Discard them and leave the editor?';

export const isCheckbox = (field: TemplateFormField) =>
  field.kind === TemplateFormFieldKind.Checkbox;

export const isChecked = (field: TemplateFormField, value: string) =>
  !!field.exportValue && value === field.exportValue;

export const checkboxValue = (field: TemplateFormField, checked: boolean) =>
  checked && field.exportValue ? field.exportValue : UNCHECKED_VALUE;

export const currentValue = (field: TemplateFormField, edits: TemplateFormEdits) =>
  edits[field.widgetName] ?? field.value;

/**
 * Whether the value on screen differs from the saved one. Checkboxes compare
 * checked states: a stored unchecked box is not always "Off", so toggling one
 * twice must not count as a change.
 */
export const isDirty = (field: TemplateFormField, edits: TemplateFormEdits) => {
  const edit = edits[field.widgetName];
  if (edit === undefined) return false;
  if (isCheckbox(field)) return isChecked(field, edit) !== isChecked(field, field.value);
  return edit !== field.value;
};

/** The save payload: only the widgets whose values changed. */
export const changedFields = (
  fields: readonly TemplateFormField[],
  edits: TemplateFormEdits
): TemplateFormFieldEditInput[] =>
  fields
    .filter((field) => isDirty(field, edits))
    .map((field) => ({ widgetName: field.widgetName, value: edits[field.widgetName] }));

/** Cara's suggestion for field, unless the user already kept their value over this same one. */
export const openSuggestion = (field: TemplateFormField, declined: DeclinedSuggestions) =>
  field.suggestion != null && declined[field.widgetName] !== field.suggestion
    ? field.suggestion
    : null;

export type ProvenanceTone = 'user' | 'cara' | 'none';

/** The server reports CARA_FILLED only for non-empty values, so empty fields Cara left read as untouched. */
export const provenanceTone = (field: TemplateFormField): ProvenanceTone => {
  switch (field.provenance) {
    case TemplateFormFieldProvenance.UserEdited:
      return 'user';
    case TemplateFormFieldProvenance.CaraFilled:
      return 'cara';
    default:
      return 'none';
  }
};

const formatTimestamp = (value: Date | null | undefined) => {
  if (!value) return null;
  // The Time scalar is typed as a Date but arrives as an ISO string.
  const date = new Date(value as unknown as string);
  if (isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

/** A user by name, or by email when they have none; null is one who no longer exists. */
const authorName = (user: { name: string; email: string } | null | undefined) =>
  user?.name || user?.email || 'a former user';

/** Who set the field's saved value, and when, for the hover card. */
export const describeLastEdit = (field: TemplateFormField) => {
  const when = formatTimestamp(field.lastEditedAt);
  const suffix = when ? ` on ${when}` : '';
  switch (field.provenance) {
    case TemplateFormFieldProvenance.UserEdited:
      return `Edited by ${authorName(field.lastEditedBy)}${suffix}`;
    case TemplateFormFieldProvenance.CaraFilled:
      return `Filled by Cara${suffix}`;
    default:
      return 'Not filled in';
  }
};

type FormVersion = Pick<CaraWorkspaceFieldsFragment['artifacts'][0], 'createdAt' | 'templateForm'>;

export type FormVersionAuthor = {
  tone: Exclude<ProvenanceTone, 'none'>;
  label: string;
  tooltip: string;
};

/**
 * Who wrote a version of a form, and when, for the version picker. A version
 * is a user's when it records who saved it, even once they no longer exist;
 * Cara's versions record no one.
 */
export const describeFormVersion = (version: FormVersion): FormVersionAuthor => {
  const when = formatTimestamp(version.createdAt);
  const suffix = when ? ` on ${when}` : '';
  const templateForm = version.templateForm;
  const editedBy =
    templateForm?.editedByAgencyUserId != null ? authorName(templateForm.editedBy) : null;
  const restoredFrom = templateForm?.restoredFromVersion;
  if (editedBy != null && restoredFrom != null) {
    return {
      tone: 'user',
      label: `Restored from v${restoredFrom}`,
      tooltip: `Version ${restoredFrom} restored by ${editedBy}${suffix}`
    };
  }
  if (editedBy != null) {
    return {
      tone: 'user',
      label: `Saved by ${editedBy}`,
      tooltip: `Saved by ${editedBy}${suffix}`
    };
  }
  return { tone: 'cara', label: 'Cara', tooltip: `Filled by Cara${suffix}` };
};

/** A value as the hover card and suggestion popover quote it. */
export const describeValue = (field: TemplateFormField, value: string) => {
  if (isCheckbox(field)) return isChecked(field, value) ? 'Checked' : 'Unchecked';
  return value === '' ? '(blank)' : `“${value}”`;
};
